/**
 * Epic Points Timeline Chart Component
 * 
 * Interactive bar chart displaying story points delivered over time with
 * performance optimizations and accessibility features.
 * 
 * Features:
 * - Bar chart visualization using Chart.js
 * - Hover interactions with detailed tooltips
 * - Zoom and pan functionality for data exploration
 * - Performance optimizations for large datasets (>200 points)
 * - Responsive design with mobile-friendly interactions
 * - Accessibility support with proper ARIA labels
 * 
 * Performance Optimizations:
 * - Data sampling for large datasets to maintain smooth rendering
 * - Memoized chart options and data processing
 * - Conditional animations based on dataset size
 * - Optimized tooltip callbacks
 * 
 * @param data - Array of timeline data points with epic information
 * @param loading - Whether the chart is in loading state
 * @param onDataPointHover - Callback fired when user hovers over a data point
 * 
 * @example
 * ```tsx
 * <EpicPointsTimelineChart
 *   data={timelineData}
 *   loading={false}
 *   onDataPointHover={(epic) => console.log('Hovered:', epic.epicKey)}
 * />
 * ```
 */

import React, { useMemo, memo, useCallback } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions,
  TooltipItem,
  TimeScale,
  Filler
} from 'chart.js'
import { Line, Bar } from 'react-chartjs-2'
import zoomPlugin from 'chartjs-plugin-zoom'
import 'chartjs-adapter-luxon'
import { EpicPointsTimelineChartProps, EpicTimelineDataPoint } from '../../../../types/epic-analytics-types'

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler,
  zoomPlugin
)

/**
 * Optimizes data for large datasets by sampling points intelligently
 */
const optimizeDataForChart = (data: EpicTimelineDataPoint[], maxPoints: number = 200): EpicTimelineDataPoint[] => {
  if (data.length <= maxPoints) {
    return data
  }

  // Always include first and last points
  const result: EpicTimelineDataPoint[] = [data[0]]

  // Calculate step size for sampling
  const step = Math.floor(data.length / (maxPoints - 2))

  // Sample intermediate points
  for (let i = step; i < data.length - 1; i += step) {
    result.push(data[i])
  }

  // Always include the last point
  result.push(data[data.length - 1])

  return result
}

/**
 * Epic Points Timeline Chart - Story points delivered over time visualization
 * Memoized for performance optimization
 */
const EpicPointsTimelineChart: React.FC<EpicPointsTimelineChartProps> = memo(({
  data,
  loading = false,
  onDataPointHover
}) => {
  // Memoize processed and optimized data
  const processedData = useMemo(() => {
    if (!data || data.length === 0) {
      return []
    }

    // Sort data by date to ensure proper timeline
    const sortedData = [...data].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())

    // Optimize for performance if dataset is large
    return data.length > 200 ? optimizeDataForChart(sortedData) : sortedData
  }, [data])

  // Use bar chart for better data visibility
  const chartType = 'bar'

  // Prepare chart data with bar chart configuration
  const chartData = useMemo<ChartData<'bar'>>(() => {
    if (processedData.length === 0) {
      return {
        labels: [],
        datasets: []
      }
    }

    // Debug: Log the data to see what we're working with
    console.log('Timeline chart data:', processedData)
    const labels = processedData.map(point => point.date)
    const data = processedData.map(point => point.pointsDelivered)
    console.log('Chart labels:', labels)
    console.log('Chart data values:', data)

    return {
      labels,
      datasets: [
        {
          label: 'Story Points Delivered',
          data,
          backgroundColor: 'rgba(59, 130, 246, 0.8)',
          borderColor: 'rgb(59, 130, 246)',
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false,
        }
      ]
    }
  }, [processedData])

  // Memoized hover handler
  const handleHover = useCallback((event: any, elements: any, chart: any) => {
    // Change cursor on hover
    if (chart.canvas) {
      chart.canvas.style.cursor = elements.length > 0 ? 'pointer' : 'default'
    }

    // Call custom hover handler
    if (elements.length > 0 && onDataPointHover) {
      const elementIndex = elements[0].index
      const dataPoint = processedData[elementIndex]
      onDataPointHover(dataPoint)
    }
  }, [processedData, onDataPointHover])

  // Memoized click handler
  const handleClick = useCallback((event: any, elements: any) => {
    if (elements.length > 0) {
      const elementIndex = elements[0].index
      const dataPoint = processedData[elementIndex]
      // Could trigger navigation to epic details
      console.log('Epic clicked:', dataPoint.epicKey)
    }
  }, [processedData])

  // Chart options with bar chart configuration
  const chartOptions = useMemo<ChartOptions<'bar'>>(() => ({
    responsive: true,
    maintainAspectRatio: false,
    // Performance optimizations
    animation: {
      duration: processedData.length > 50 ? 0 : 750, // Disable animation for large datasets
    },
    interaction: {
      intersect: false,
      mode: 'index'
    },
    elements: {
      bar: {
        borderRadius: 4,
      }
    },
    plugins: {
      legend: {
        display: false // Hide legend since we only have one dataset
      },
      title: {
        display: false // Title is handled by the component wrapper
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgb(59, 130, 246)',
        borderWidth: 2,
        cornerRadius: 12,
        displayColors: false,
        padding: 12,
        titleFont: {
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          size: 12
        },
        // Enhanced tooltip positioning
        position: 'nearest',
        caretPadding: 8,
        callbacks: {
          title: (tooltipItems: TooltipItem<'bar'>[]) => {
            const item = tooltipItems[0]
            const dataPoint = processedData[item.dataIndex]
            return `${dataPoint.epicKey}`
          },
          beforeBody: (tooltipItems: TooltipItem<'bar'>[]) => {
            const item = tooltipItems[0]
            const dataPoint = processedData[item.dataIndex]
            // Truncate long titles
            const maxLength = 50
            const title = dataPoint.epicTitle
            return title.length > maxLength ? `${title.substring(0, maxLength)}...` : title
          },
          label: (tooltipItem: TooltipItem<'bar'>) => {
            const dataPoint = processedData[tooltipItem.dataIndex]
            return [
              `Completion: ${new Date(dataPoint.date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              })}`,
              `Points Delivered: ${dataPoint.pointsDelivered}`,
              `Cumulative Total: ${dataPoint.cumulativePoints}`
            ]
          },
          afterBody: () => {
            return ['', 'Click to view epic details']
          }
        },
        // Custom tooltip styling based on data
        filter: (tooltipItem) => {
          // Only show tooltip for actual data points
          return tooltipItem.dataIndex < processedData.length
        }
      },
      zoom: {
        limits: {
          x: {
            min: 'original',
            max: 'original'
          },
          y: {
            min: 0,
            max: 'original'
          }
        },
        pan: {
          enabled: true,
          mode: 'x',
          threshold: 10,
          // Smooth panning
          modifierKey: undefined
        },
        zoom: {
          wheel: {
            enabled: true,
            speed: 0.1
          },
          pinch: {
            enabled: true
          },
          mode: 'x',
          // Zoom limits
          sensitivity: 3,
          speed: 0.1
        }
      }
    },
    scales: {
      x: {
        type: 'category',
        title: {
          display: true,
          text: 'Epic Completion Date',
          font: {
            size: 12,
            weight: 'bold'
          },
          color: 'rgb(75, 85, 99)' // gray-600
        },
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.05)',
          lineWidth: 1
        },
        ticks: {
          maxTicksLimit: processedData.length > 50 ? 8 : 12, // Fewer ticks for large datasets
          color: 'rgb(107, 114, 128)' // gray-500
        }
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Story Points Delivered',
          font: {
            size: 12,
            weight: 'bold'
          },
          color: 'rgb(75, 85, 99)' // gray-600
        },
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.05)',
          lineWidth: 1
        },
        ticks: {
          precision: 0,
          color: 'rgb(107, 114, 128)', // gray-500
          // Format large numbers
          callback: function (value) {
            if (typeof value === 'number') {
              return value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value.toString()
            }
            return value
          }
        }
      }
    },
    // Enhanced hover interactions
    onHover: handleHover,
    // Click handler for navigation
    onClick: handleClick
  }), [processedData, handleHover, handleClick])

  // Memoized loading component for performance
  const LoadingComponent = useMemo(() => (
    <div className="epic-points-timeline-chart">
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    </div>
  ), [])

  // Memoized empty state component for performance
  const EmptyStateComponent = useMemo(() => (
    <div className="epic-points-timeline-chart">
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Story Points Delivered Over Time
        </h3>
        <div className="h-64 flex items-center justify-center bg-gray-50 rounded">
          <div className="text-center">
            <p className="text-gray-500 mb-2">No epic data available</p>
            <p className="text-sm text-gray-400">
              Select a project and time period to view the timeline
            </p>
          </div>
        </div>
      </div>
    </div>
  ), [])

  if (loading) {
    return LoadingComponent
  }

  if (!data || data.length === 0) {
    return EmptyStateComponent
  }

  return (
    <div className="epic-points-timeline-chart">
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Story Points Delivered Over Time
        </h3>
        <div className="h-64 relative">
          <Bar data={chartData} options={chartOptions} />
        </div>
        <div className="mt-2 text-xs text-gray-500 text-center">
          Hover over bars for epic details • Scroll to zoom • Drag to pan
          {data.length > processedData.length && (
            <span className="ml-2 text-amber-600">
              • Showing {processedData.length} of {data.length} points for performance
            </span>
          )}
        </div>
      </div>
    </div>
  )
}, (prevProps, nextProps) => {
  // Custom comparison function for memo optimization
  return (
    prevProps.loading === nextProps.loading &&
    prevProps.data === nextProps.data &&
    prevProps.onDataPointHover === nextProps.onDataPointHover
  )
})

EpicPointsTimelineChart.displayName = 'EpicPointsTimelineChart'

export { EpicPointsTimelineChart }
export default EpicPointsTimelineChart